/**
 * SelectAllController - Manages hierarchical checkbox selection behavior
 *
 * This Stimulus controller handles complex checkbox selection patterns with parent-child relationships.
 * It supports nested containers where parent checkboxes can control child checkboxes, and child
 * checkbox states automatically update their parent checkbox states.
 *
 * Features:
 * - Parent checkboxes can select/deselect all children in their container
 * - Parent checkbox state reflects child checkbox states (checked when all/any children are checked)
 * - Supports nested containers for multi-level hierarchies
 * - Two selection modes: "select_all" (requires ALL children) and default (requires ANY child)
 *
 * Required HTML Structure:
 *
 * <div data-controller="select-all">
 *   <div data-selectable-container="main_container">
 *     <!-- Parent checkbox with select_all mode: checked only when ALL children are checked -->
 *     <input type="checkbox"
 *            data-action="change->select-all#call"
 *            data-select-container="main_container"
 *            data-select-mode="select_all">
 *
 *     <div data-selectable-container="sub_container_a">
 *       <!-- Parent checkbox (default mode): checked when ANY children are checked -->
 *       <input type="checkbox"
 *              data-action="change->select-all#call"
 *              data-select-container="sub_container_a">
 *
 *       <!-- Child checkboxes (no data-select-container attribute) -->
 *       <input type="checkbox" data-action="change->select-all#call">
 *       <input type="checkbox" data-action="change->select-all#call">
 *     </div>
 *   </div>
 * </div>
 *
 * Data Attributes:
 * - data-select-container: Identifies a parent checkbox that controls children in the named container
 * - data-selectable-container: Defines a container that groups related checkboxes
 * - data-select-mode="select_all": Parent checkbox checked only when ALL children are checked (default: ANY)
 * - data-action="change->select-all#call": Triggers the selection logic on checkbox change
 */

import { Controller } from '@hotwired/stimulus';

export default class extends Controller {
  call(event) {
    const containerName = event.currentTarget.dataset.selectContainer;

    if (containerName) this.#toggleChildCheckboxes(containerName, event.currentTarget.checked);

    this.#toggleParentCheckboxes(event);
  }

  #toggleChildCheckboxes(containerName, checked) {
    document
      .querySelectorAll(`[data-selectable-container=${containerName}]`)[0]
      .querySelectorAll('input[type="checkbox"]')
      .forEach((checkbox) => checkbox.checked = checked);
  }

  #toggleParentCheckboxes(event) {
    const selectableContainers = this.#getSelectableContainers(event.currentTarget);

    selectableContainers.forEach((container) => {
      const dataAttr = `[data-select-container="${container.dataset.selectableContainer}"]`;

      const parentCheckbox = container.querySelectorAll(`input[type="checkbox"]${dataAttr}`)[0];
      const allChildCheckboxesCount = container.querySelectorAll(`input[type="checkbox"]:not(${dataAttr})`).length;
      const checkedChildCheckboxesCount = container.querySelectorAll(`input[type="checkbox"]:not(${dataAttr}):checked`).length;

      if (parentCheckbox.dataset.selectMode === 'select_all') {
        parentCheckbox.checked = checkedChildCheckboxesCount === allChildCheckboxesCount;
      } else {
        parentCheckbox.checked = checkedChildCheckboxesCount > 0;
      }
    });
  }

  #getSelectableContainers(element) {
    const result = [];
    let current = element;

    while (true) {
      const nextElement = current.closest('[data-selectable-container]');

      if (nextElement) {
        result.push(nextElement);
        current = nextElement.parentElement;
      } else {
        break;
      }
    }

    return result;
  }
}
